import { useState, useEffect, useCallback } from 'react';
import { Settings, Save, Copy, CheckCircle, AlertTriangle, ChevronDown, ChevronRight, Loader2 } from 'lucide-react';
import { superAdminApi } from '../lib/api';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface TaxSettings {
  year: number;
  isActive: boolean;

  // Wskazniki bazowe
  minimumWage: number;
  averageWage: number;
  avgWageSectorQ4: number;

  // PIT
  pitLinearRate: number;
  pitScaleThreshold: number;
  pitScaleRateBelow: number;
  pitScaleRateAbove: number;
  pitScaleTaxFree: number;

  // ZUS stawki procentowe
  zusEmerytalnaRate: number;
  zusRentowaRate: number;
  zusChorobowaRate: number;
  zusWypadkowaRate: number;
  zusFunduszPracyRate: number;

  // ZUS kwoty
  zusFullBase: number;
  zusFullTotal: number;
  zusPreferentialBase: number;
  zusPreferentialTotal: number;

  // Skladka zdrowotna
  healthScaleRate: number;
  healthLinearRate: number;
  healthMinimum: number;
  healthLumpThreshold1: number;
  healthLumpThreshold2: number;
  healthLumpAmount1: number;
  healthLumpAmount2: number;
  healthLumpAmount3: number;
  healthLinearDeductionLimit: number;

  // CIT
  citSmallRate: number;
  citStandardRate: number;
  citEstonianSmall: number;
  citEstonianStandard: number;

  // VAT
  vatStandard: number;
  vatReduced1: number;
  vatReduced2: number;
  vatZero: number;
  vatExemptionLimit: number;
}

interface YearSummary {
  year: number;
  isActive: boolean;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function round2(v: number): number {
  return Math.round(v * 100) / 100;
}

function toNumber(v: unknown): number {
  if (typeof v === 'number') return v;
  if (typeof v === 'string') {
    const n = parseFloat(v);
    return isNaN(n) ? 0 : n;
  }
  return 0;
}

function normalizeSettings(raw: Record<string, unknown>): TaxSettings {
  const fields: (keyof TaxSettings)[] = [
    'year', 'isActive',
    'minimumWage', 'averageWage', 'avgWageSectorQ4',
    'pitLinearRate', 'pitScaleThreshold', 'pitScaleRateBelow', 'pitScaleRateAbove', 'pitScaleTaxFree',
    'zusEmerytalnaRate', 'zusRentowaRate', 'zusChorobowaRate', 'zusWypadkowaRate', 'zusFunduszPracyRate',
    'zusFullBase', 'zusFullTotal', 'zusPreferentialBase', 'zusPreferentialTotal',
    'healthScaleRate', 'healthLinearRate', 'healthMinimum',
    'healthLumpThreshold1', 'healthLumpThreshold2',
    'healthLumpAmount1', 'healthLumpAmount2', 'healthLumpAmount3',
    'healthLinearDeductionLimit',
    'citSmallRate', 'citStandardRate', 'citEstonianSmall', 'citEstonianStandard',
    'vatStandard', 'vatReduced1', 'vatReduced2', 'vatZero', 'vatExemptionLimit',
  ];
  const result: Record<string, unknown> = {};
  for (const key of fields) {
    if (key === 'isActive') {
      result[key] = !!raw[key];
    } else {
      result[key] = toNumber(raw[key]);
    }
  }
  return result as unknown as TaxSettings;
}

// ---------------------------------------------------------------------------
// CollapsibleSection component
// ---------------------------------------------------------------------------

function CollapsibleSection({
  title,
  icon,
  children,
  defaultOpen = true,
}: {
  title: string;
  icon?: React.ReactNode;
  children: React.ReactNode;
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div className="bg-surface-raised/50 border border-border-subtle rounded-xl overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-6 py-4 text-left hover:bg-surface-hover/30 transition-colors"
      >
        <div className="flex items-center gap-3">
          {icon}
          <h3 className="text-base font-semibold text-text-primary">{title}</h3>
        </div>
        {open ? (
          <ChevronDown className="w-5 h-5 text-text-secondary" />
        ) : (
          <ChevronRight className="w-5 h-5 text-text-secondary" />
        )}
      </button>
      {open && (
        <div className="px-6 pb-6 pt-2 border-t border-border-subtle">
          {children}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// NumberField component
// ---------------------------------------------------------------------------

function NumberField({
  label,
  value,
  onChange,
  unit,
  step,
  disabled,
  highlight,
}: {
  label: string;
  value: number;
  onChange: (v: number) => void;
  unit?: string;
  step?: number;
  disabled?: boolean;
  highlight?: boolean;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-xs font-medium text-text-secondary">{label}</label>
      <div className="relative">
        <input
          type="number"
          value={value}
          onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
          step={step ?? 0.01}
          disabled={disabled}
          className={`
            w-full px-3 py-2.5 rounded-lg text-sm text-text-primary
            border focus:outline-none focus:ring-2 transition-colors
            ${highlight
              ? 'bg-warning/10 border-warning/30 focus:border-yellow-500 focus:ring-yellow-500/20'
              : 'bg-surface-base border-border focus:border-accent-primary focus:ring-accent-primary/20'
            }
            ${disabled ? 'opacity-60 cursor-not-allowed' : ''}
          `}
        />
        {unit && (
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-text-muted pointer-events-none">
            {unit}
          </span>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main Page
// ---------------------------------------------------------------------------

export function SuperAdminTaxSettingsPage() {
  const [years, setYears] = useState<YearSummary[]>([]);
  const [selectedYear, setSelectedYear] = useState<number | null>(null);
  const [settings, setSettings] = useState<TaxSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activating, setActivating] = useState(false);
  const [cloning, setCloning] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [showBaseWarning, setShowBaseWarning] = useState(false);
  const [dirty, setDirty] = useState(false);

  // ------ Load years list ------
  const loadYears = useCallback(async () => {
    try {
      const res = await superAdminApi.getTaxSettings();
      const data = res.data as YearSummary[] | { settings: YearSummary[] } | { data: YearSummary[] };
      let list: YearSummary[];
      if (Array.isArray(data)) {
        list = data;
      } else if (Array.isArray((data as Record<string, unknown>).settings)) {
        list = (data as { settings: YearSummary[] }).settings;
      } else if (Array.isArray((data as Record<string, unknown>).data)) {
        list = (data as { data: YearSummary[] }).data;
      } else {
        list = [];
      }
      list.sort((a, b) => b.year - a.year);
      setYears(list);
      return list;
    } catch (err) {
      console.error('Failed to load tax settings years:', err);
      setError('Nie udalo sie zaladowac listy lat podatkowych');
      return [];
    }
  }, []);

  // ------ Load specific year ------
  const loadYear = useCallback(async (year: number) => {
    try {
      setLoading(true);
      setError('');
      const res = await superAdminApi.getTaxSettingsForYear(year);
      const raw = (res.data as Record<string, unknown>).settings
        ? ((res.data as Record<string, unknown>).settings as Record<string, unknown>)
        : (res.data as Record<string, unknown>);
      const normalized = normalizeSettings(raw);
      setSettings(normalized);
      setSelectedYear(year);
      setDirty(false);
      setShowBaseWarning(false);
    } catch (err) {
      console.error('Failed to load tax settings for year:', err);
      setError(`Nie udalo sie zaladowac ustawien dla roku ${year}`);
    } finally {
      setLoading(false);
    }
  }, []);

  // ------ Initial load ------
  useEffect(() => {
    (async () => {
      const list = await loadYears();
      if (list.length > 0) {
        const activeYear = list.find((y) => y.isActive);
        await loadYear(activeYear ? activeYear.year : list[0].year);
      } else {
        // No years yet -- try seeding
        try {
          await superAdminApi.seedTaxSettings();
          const seeded = await loadYears();
          if (seeded.length > 0) {
            await loadYear(seeded[0].year);
          }
        } catch {
          setLoading(false);
        }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ------ Update a field ------
  const updateField = useCallback(
    (field: keyof TaxSettings, value: number) => {
      setSettings((prev) => {
        if (!prev) return prev;
        const next = { ...prev, [field]: value };

        // Auto-recalculation when base indicators change
        const baseFields: (keyof TaxSettings)[] = ['minimumWage', 'averageWage'];
        if (baseFields.includes(field)) {
          const mw = field === 'minimumWage' ? value : next.minimumWage;
          const aw = field === 'averageWage' ? value : next.averageWage;

          next.zusFullBase = round2(aw * 0.60);
          next.zusPreferentialBase = round2(mw * 0.30);
          next.healthMinimum = round2(mw * 0.09);
          setShowBaseWarning(true);
        }

        setDirty(true);
        return next;
      });
    },
    [],
  );

  // ------ Clear alerts ------
  const clearAlerts = useCallback(() => {
    setSuccess('');
    setError('');
  }, []);

  // ------ Save ------
  const handleSave = async () => {
    if (!settings || selectedYear === null) return;
    clearAlerts();
    setSaving(true);
    try {
      const { year: _y, isActive: _a, ...data } = settings;
      await superAdminApi.updateTaxSettings(selectedYear, data);
      setSuccess('Ustawienia podatkowe zostaly zapisane pomyslnie');
      setDirty(false);
      setShowBaseWarning(false);
      await loadYears();
    } catch (err) {
      console.error('Save failed:', err);
      setError('Nie udalo sie zapisac ustawien');
    } finally {
      setSaving(false);
    }
  };

  // ------ Activate ------
  const handleActivate = async () => {
    if (selectedYear === null) return;
    clearAlerts();
    setActivating(true);
    try {
      await superAdminApi.activateTaxSettings(selectedYear);
      setSuccess(`Rok ${selectedYear} zostal aktywowany`);
      setSettings((prev) => (prev ? { ...prev, isActive: true } : prev));
      await loadYears();
    } catch (err) {
      console.error('Activate failed:', err);
      setError('Nie udalo sie aktywowac roku');
    } finally {
      setActivating(false);
    }
  };

  // ------ Clone ------
  const handleClone = async () => {
    if (selectedYear === null) return;
    clearAlerts();
    setCloning(true);
    try {
      const targetYear = selectedYear + 1;
      await superAdminApi.cloneTaxSettings(selectedYear, targetYear);
      setSuccess(`Ustawienia zostaly sklonowane do roku ${targetYear}`);
      const updatedYears = await loadYears();
      if (updatedYears.find((y) => y.year === targetYear)) {
        await loadYear(targetYear);
      }
    } catch (err) {
      console.error('Clone failed:', err);
      setError('Nie udalo sie sklonowac ustawien. Rok docelowy moze juz istniec.');
    } finally {
      setCloning(false);
    }
  };

  // ------ Year switch ------
  const handleYearChange = async (year: number) => {
    if (year === selectedYear) return;
    clearAlerts();
    await loadYear(year);
  };

  // ------ Render ------
  if (loading && !settings) {
    return (
      <div className="flex items-center justify-center py-32">
        <Loader2 className="w-8 h-8 animate-spin text-accent-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-text-primary flex items-center gap-3">
            <Settings className="w-8 h-8 text-accent-primary" />
            Globalne ustawienia podatkowe
          </h1>
          <p className="text-text-secondary mt-2">
            Zarzadzaj wskaznikami podatkowymi i skladkami ZUS dla wszystkich tenantow
          </p>
        </div>
      </div>

      {/* Year selector + active badge */}
      <div className="flex flex-wrap items-center gap-4 p-4 bg-surface-raised/50 border border-border-subtle rounded-xl">
        <div className="flex flex-col gap-1">
          <label className="text-xs text-text-secondary">Rok podatkowy</label>
          <select
            value={selectedYear ?? ''}
            onChange={(e) => handleYearChange(parseInt(e.target.value))}
            className="bg-surface-base border border-border rounded-lg px-4 py-2.5 text-text-primary text-sm focus:outline-none focus:border-accent-primary/50 transition-colors min-w-[140px]"
          >
            {years.map((y) => (
              <option key={y.year} value={y.year}>
                {y.year} {y.isActive ? '(aktywny)' : ''}
              </option>
            ))}
          </select>
        </div>

        {settings && settings.isActive && (
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-green-500/15 text-success border border-success/30">
            <CheckCircle className="w-3.5 h-3.5" />
            Aktywny rok
          </span>
        )}

        {settings && !settings.isActive && (
          <button
            onClick={handleActivate}
            disabled={activating}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium bg-green-500/15 text-success border border-success/30 hover:bg-green-500/25 transition-colors disabled:opacity-50"
          >
            {activating ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <CheckCircle className="w-4 h-4" />
            )}
            Aktywuj
          </button>
        )}

        <button
          onClick={handleClone}
          disabled={cloning || selectedYear === null}
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium bg-accent-primary/10 text-accent-primary border border-accent-primary/30 hover:bg-accent-primary/20 transition-colors disabled:opacity-50 ml-auto"
        >
          {cloning ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Copy className="w-4 h-4" />
          )}
          Dodaj nowy rok
        </button>
      </div>

      {/* Alerts */}
      {error && (
        <div className="flex items-center p-4 bg-danger/10 border border-danger/30 rounded-xl text-danger">
          <AlertTriangle className="w-5 h-5 mr-3 flex-shrink-0" />
          <span className="text-sm">{error}</span>
        </div>
      )}
      {success && (
        <div className="flex items-center p-4 bg-success/10 border border-success/30 rounded-xl text-success">
          <CheckCircle className="w-5 h-5 mr-3 flex-shrink-0" />
          <span className="text-sm">{success}</span>
        </div>
      )}
      {showBaseWarning && (
        <div className="flex items-center p-4 bg-warning/10 border border-warning/30 rounded-xl text-warning">
          <AlertTriangle className="w-5 h-5 mr-3 flex-shrink-0" />
          <span className="text-sm">
            Zmiana wskaznikow wplynie na obliczone wartosci (podstawy ZUS i skladka zdrowotna minimalna zostaly przeliczone automatycznie)
          </span>
        </div>
      )}

      {settings && (
        <>
          {/* ================================================================ */}
          {/* Wskazniki bazowe */}
          {/* ================================================================ */}
          <CollapsibleSection
            title="Wskazniki bazowe"
            icon={<span className="w-2 h-2 rounded-full bg-accent-primary inline-block" />}
          >
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <NumberField
                label="Placa minimalna (minimumWage)"
                value={settings.minimumWage}
                onChange={(v) => updateField('minimumWage', v)}
                unit="zl/mies."
                highlight={showBaseWarning}
              />
              <NumberField
                label="Przecietne wynagrodzenie (averageWage)"
                value={settings.averageWage}
                onChange={(v) => updateField('averageWage', v)}
                unit="zl/mies."
                highlight={showBaseWarning}
              />
              <NumberField
                label="Przecietne w sektorze IV kw. (avgWageSectorQ4)"
                value={settings.avgWageSectorQ4}
                onChange={(v) => updateField('avgWageSectorQ4', v)}
                unit="zl/mies."
              />
            </div>
          </CollapsibleSection>

          {/* ================================================================ */}
          {/* PIT */}
          {/* ================================================================ */}
          <CollapsibleSection
            title="PIT"
            icon={<span className="w-2 h-2 rounded-full bg-purple-400 inline-block" />}
          >
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
              <NumberField
                label="Stawka liniowa (pitLinearRate)"
                value={settings.pitLinearRate}
                onChange={(v) => updateField('pitLinearRate', v)}
                unit="%"
              />
              <NumberField
                label="Prog skali (pitScaleThreshold)"
                value={settings.pitScaleThreshold}
                onChange={(v) => updateField('pitScaleThreshold', v)}
                unit="zl/rok"
              />
              <NumberField
                label="Stawka ponizej progu (pitScaleRateBelow)"
                value={settings.pitScaleRateBelow}
                onChange={(v) => updateField('pitScaleRateBelow', v)}
                unit="%"
              />
              <NumberField
                label="Stawka powyzej progu (pitScaleRateAbove)"
                value={settings.pitScaleRateAbove}
                onChange={(v) => updateField('pitScaleRateAbove', v)}
                unit="%"
              />
              <NumberField
                label="Kwota wolna (pitScaleTaxFree)"
                value={settings.pitScaleTaxFree}
                onChange={(v) => updateField('pitScaleTaxFree', v)}
                unit="zl/rok"
              />
            </div>
          </CollapsibleSection>

          {/* ================================================================ */}
          {/* ZUS stawki procentowe */}
          {/* ================================================================ */}
          <CollapsibleSection
            title="ZUS stawki procentowe"
            icon={<span className="w-2 h-2 rounded-full bg-blue-400 inline-block" />}
          >
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
              <NumberField
                label="Emerytalna (zusEmerytalnaRate)"
                value={settings.zusEmerytalnaRate}
                onChange={(v) => updateField('zusEmerytalnaRate', v)}
                unit="%"
              />
              <NumberField
                label="Rentowa (zusRentowaRate)"
                value={settings.zusRentowaRate}
                onChange={(v) => updateField('zusRentowaRate', v)}
                unit="%"
              />
              <NumberField
                label="Chorobowa (zusChorobowaRate)"
                value={settings.zusChorobowaRate}
                onChange={(v) => updateField('zusChorobowaRate', v)}
                unit="%"
              />
              <NumberField
                label="Wypadkowa (zusWypadkowaRate)"
                value={settings.zusWypadkowaRate}
                onChange={(v) => updateField('zusWypadkowaRate', v)}
                unit="%"
              />
              <NumberField
                label="Fundusz Pracy (zusFunduszPracyRate)"
                value={settings.zusFunduszPracyRate}
                onChange={(v) => updateField('zusFunduszPracyRate', v)}
                unit="%"
              />
            </div>
          </CollapsibleSection>

          {/* ================================================================ */}
          {/* ZUS kwoty */}
          {/* ================================================================ */}
          <CollapsibleSection
            title="ZUS kwoty (auto-kalkulacja z podstaw)"
            icon={<span className="w-2 h-2 rounded-full bg-indigo-400 inline-block" />}
          >
            <p className="text-xs text-text-muted mb-4">
              Podstawy sa przeliczane automatycznie przy zmianie wskaznikow bazowych:
              zusFullBase = 60% x averageWage, zusPreferentialBase = 30% x minimumWage
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <NumberField
                label="Podstawa pelna ZUS (zusFullBase)"
                value={settings.zusFullBase}
                onChange={(v) => updateField('zusFullBase', v)}
                unit="zl"
                highlight={showBaseWarning}
              />
              <NumberField
                label="Pelny ZUS total (zusFullTotal)"
                value={settings.zusFullTotal}
                onChange={(v) => updateField('zusFullTotal', v)}
                unit="zl"
              />
              <NumberField
                label="Podstawa preferencyjna (zusPreferentialBase)"
                value={settings.zusPreferentialBase}
                onChange={(v) => updateField('zusPreferentialBase', v)}
                unit="zl"
                highlight={showBaseWarning}
              />
              <NumberField
                label="Preferencyjny ZUS total (zusPreferentialTotal)"
                value={settings.zusPreferentialTotal}
                onChange={(v) => updateField('zusPreferentialTotal', v)}
                unit="zl"
              />
            </div>
          </CollapsibleSection>

          {/* ================================================================ */}
          {/* Skladka zdrowotna */}
          {/* ================================================================ */}
          <CollapsibleSection
            title="Skladka zdrowotna"
            icon={<span className="w-2 h-2 rounded-full bg-pink-400 inline-block" />}
          >
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <NumberField
                label="Stawka skala (healthScaleRate)"
                value={settings.healthScaleRate}
                onChange={(v) => updateField('healthScaleRate', v)}
                unit="%"
              />
              <NumberField
                label="Stawka liniowa (healthLinearRate)"
                value={settings.healthLinearRate}
                onChange={(v) => updateField('healthLinearRate', v)}
                unit="%"
              />
              <NumberField
                label="Minimalna (healthMinimum)"
                value={settings.healthMinimum}
                onChange={(v) => updateField('healthMinimum', v)}
                unit="zl"
                highlight={showBaseWarning}
              />
            </div>

            <h4 className="text-sm font-medium text-text-secondary mb-3 mt-6">Progi ryczaltu</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-4">
              <NumberField
                label="Prog 1 (healthLumpThreshold1)"
                value={settings.healthLumpThreshold1}
                onChange={(v) => updateField('healthLumpThreshold1', v)}
                unit="zl/rok"
              />
              <NumberField
                label="Prog 2 (healthLumpThreshold2)"
                value={settings.healthLumpThreshold2}
                onChange={(v) => updateField('healthLumpThreshold2', v)}
                unit="zl/rok"
              />
              <NumberField
                label="Kwota tier 1 (healthLumpAmount1)"
                value={settings.healthLumpAmount1}
                onChange={(v) => updateField('healthLumpAmount1', v)}
                unit="zl"
              />
              <NumberField
                label="Kwota tier 2 (healthLumpAmount2)"
                value={settings.healthLumpAmount2}
                onChange={(v) => updateField('healthLumpAmount2', v)}
                unit="zl"
              />
              <NumberField
                label="Kwota tier 3 (healthLumpAmount3)"
                value={settings.healthLumpAmount3}
                onChange={(v) => updateField('healthLumpAmount3', v)}
                unit="zl"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <NumberField
                label="Limit odliczenia liniowa (healthLinearDeductionLimit)"
                value={settings.healthLinearDeductionLimit}
                onChange={(v) => updateField('healthLinearDeductionLimit', v)}
                unit="zl/rok"
              />
            </div>
          </CollapsibleSection>

          {/* ================================================================ */}
          {/* CIT */}
          {/* ================================================================ */}
          <CollapsibleSection
            title="CIT"
            icon={<span className="w-2 h-2 rounded-full bg-orange-400 inline-block" />}
          >
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <NumberField
                label="Maly CIT (citSmallRate)"
                value={settings.citSmallRate}
                onChange={(v) => updateField('citSmallRate', v)}
                unit="%"
              />
              <NumberField
                label="Standardowy CIT (citStandardRate)"
                value={settings.citStandardRate}
                onChange={(v) => updateField('citStandardRate', v)}
                unit="%"
              />
              <NumberField
                label="Estonski maly (citEstonianSmall)"
                value={settings.citEstonianSmall}
                onChange={(v) => updateField('citEstonianSmall', v)}
                unit="%"
              />
              <NumberField
                label="Estonski standardowy (citEstonianStandard)"
                value={settings.citEstonianStandard}
                onChange={(v) => updateField('citEstonianStandard', v)}
                unit="%"
              />
            </div>
          </CollapsibleSection>

          {/* ================================================================ */}
          {/* VAT */}
          {/* ================================================================ */}
          <CollapsibleSection
            title="VAT"
            icon={<span className="w-2 h-2 rounded-full bg-green-400 inline-block" />}
          >
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
              <NumberField
                label="Stawka standardowa (vatStandard)"
                value={settings.vatStandard}
                onChange={(v) => updateField('vatStandard', v)}
                unit="%"
              />
              <NumberField
                label="Stawka obniz. 1 (vatReduced1)"
                value={settings.vatReduced1}
                onChange={(v) => updateField('vatReduced1', v)}
                unit="%"
              />
              <NumberField
                label="Stawka obniz. 2 (vatReduced2)"
                value={settings.vatReduced2}
                onChange={(v) => updateField('vatReduced2', v)}
                unit="%"
              />
              <NumberField
                label="Stawka zerowa (vatZero)"
                value={settings.vatZero}
                onChange={(v) => updateField('vatZero', v)}
                unit="%"
              />
              <NumberField
                label="Limit zwolnienia (vatExemptionLimit)"
                value={settings.vatExemptionLimit}
                onChange={(v) => updateField('vatExemptionLimit', v)}
                unit="zl/rok"
              />
            </div>
          </CollapsibleSection>

          {/* ================================================================ */}
          {/* Action buttons */}
          {/* ================================================================ */}
          <div className="flex flex-wrap items-center justify-between gap-4 p-4 bg-surface-raised/50 border border-border-subtle rounded-xl">
            <div className="flex items-center gap-3">
              {dirty && (
                <span className="text-xs text-warning flex items-center gap-1.5">
                  <AlertTriangle className="w-3.5 h-3.5" />
                  Niezapisane zmiany
                </span>
              )}
            </div>

            <div className="flex flex-wrap items-center gap-3">
              {!settings.isActive && (
                <button
                  onClick={handleActivate}
                  disabled={activating}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium bg-green-500/15 text-success border border-success/30 hover:bg-green-500/25 transition-colors disabled:opacity-50"
                >
                  {activating ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <CheckCircle className="w-4 h-4" />
                  )}
                  Aktywuj rok
                </button>
              )}

              <button
                onClick={handleClone}
                disabled={cloning}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium bg-surface-hover/50 text-text-primary border border-accent-primary/30 hover:border-accent-primary/50 hover:bg-surface-muted/50 transition-colors disabled:opacity-50"
              >
                {cloning ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Copy className="w-4 h-4" />
                )}
                Klonuj do nowego roku
              </button>

              <button
                onClick={handleSave}
                disabled={saving || !dirty}
                className="inline-flex items-center gap-2 px-6 py-2.5 rounded-lg text-sm font-semibold bg-accent-primary text-text-primary hover:bg-accent-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Save className="w-4 h-4" />
                )}
                Zapisz
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
